import { useState, useEffect, useMemo } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Chip,
    Divider,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    useTheme,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import axiosClient from "../../axios";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

// ── Constants ──────────────────────────────────────────────────────────────────

const SURVEY_TYPES = ["new", "active", "overdue", "escalated", "submitted", "completed", "archived"];
const ACTIONABLE_TYPES = ["active", "overdue", "escalated", "submitted", "completed"];

const TYPE_CONFIG = {
    new:       { label: "New",       color: "#6870fa" },
    active:    { label: "Active",    color: "#4cceac" },
    overdue:   { label: "Overdue",   color: "#db4f4a" },
    escalated: { label: "Escalated", color: "#FF9800" },
    submitted: { label: "Submitted", color: "#00BCD4" },
    completed: { label: "Completed", color: "#2e7c67" },
    archived:  { label: "Archived",  color: "#858585" },
};

const REQ_STATUS_COLORS = {
    "Pending":         "#6870fa",
    "To be attended":  "#858585",
    "In progress":     "#FF9800",
    "Completed":       "#4cceac",
    "Waived":          "#00BCD4",
};

const fetchInspectionType = async (type) => {
    try {
        const res = await axiosClient.get(`/inspections/broker_inspections?type=${type}&per_page=200&page=1`);
        return {
            type,
            data: res.data?.data?.data || [],
            total: res.data?.data?.total || 0,
        };
    } catch {
        return { type, data: [], total: 0 };
    }
};

const collectAuditIds = (byType, types) => {
    const auditIds = [];
    types.forEach((type) => {
        const inspections = byType[type] || [];
        inspections.forEach((inspection) => {
            if (inspection?.audit_id) {
                auditIds.push(inspection.audit_id);
            }
        });
    });
    return auditIds;
};

const getDaysElapsedColors = (daysElapsed) => {
    if (daysElapsed > 30) {
        return { bgColor: "#db4f4a22", textColor: "#db4f4a" };
    }
    if (daysElapsed > 14) {
        return { bgColor: "#FF980022", textColor: "#FF9800" };
    }
    return { bgColor: "#4cceac22", textColor: "#4cceac" };
};

const getUnstartedColor = (unstarted, colors) => {
    if (unstarted > 3) return "#db4f4a";
    if (unstarted > 0) return "#FF9800";
    return colors.grey[300];
};

const createCompletionRateTooltip = (colors) => ({ data }) => (
    <Box sx={{ bgcolor: colors.grey[900], p: 1, borderRadius: 1, fontSize: 12 }}>
        <strong style={{ color: colors.grey[100] }}>{data.label}</strong>
        <br />
        <span style={{ color: colors.grey[100] }}>
            {data["% Complete"]}% complete ({data.completed}/{data.total} requirements)
        </span>
    </Box>
);

const createCompletionDurationTooltip = (colors) => ({ data }) => (
    <Box sx={{ bgcolor: colors.grey[900], p: 1, borderRadius: 1, fontSize: 12 }}>
        <strong style={{ color: colors.grey[100] }}>{data.label}</strong>
        <br />
        <span style={{ color: colors.grey[100] }}>
            {data["Days to Complete"]} days to complete
        </span>
    </Box>
);

const createMomentumTooltip = (colors) => ({ id, value, data }) => (
    <Box sx={{ bgcolor: colors.grey[900], p: 1, borderRadius: 1, fontSize: 12 }}>
        <strong style={{ color: colors.grey[100] }}>{data.label}</strong>
        <br />
        <span style={{ color: colors.grey[100] }}>
            {id}: {value}
            {id === "% Complete" ? "%" : " days"}
        </span>
    </Box>
);

// ── Helpers ────────────────────────────────────────────────────────────────────

const daysBetween = (dateA, dateB) => {
    const a = new Date(dateA);
    const b = new Date(dateB);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

const todayMidnight = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const getHorizontalChartLeftMargin = (rows, key = "survey") => {
    const longest = (rows || []).reduce((max, row) => {
        const label = String(row?.[key] || "");
        return Math.max(max, label.length);
    }, 0);

    // Approximate character width for axis labels, with sensible bounds.
    return Math.max(130, Math.min(280, longest * 7 + 30));
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function BrokerReports() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [loading, setLoading] = useState(true);
    const [surveyCounts, setSurveyCounts] = useState({});
    const [inspectionsByType, setInspectionsByType] = useState({});
    const [reqStats, setReqStats] = useState(null);
    const [reqHealthTypes, setReqHealthTypes] = useState(ACTIONABLE_TYPES);

    // ── Data fetching ────────────────────────────────────────────────────────

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // Fetch all 7 types in parallel
                const results = await Promise.all(SURVEY_TYPES.map(fetchInspectionType));

                const byType = {};
                const counts = {};
                results.forEach(({ type, data, total }) => {
                    byType[type] = data;
                    counts[type] = total;
                });

                setInspectionsByType(byType);
                setSurveyCounts(counts);

                // Get requirements stats for actionable survey types only (not "new" or "archived")
                const auditIds = collectAuditIds(byType, ACTIONABLE_TYPES);

                if (auditIds.length > 0) {
                    const statsRes = await axiosClient.post(
                        "/inspections/requirements/status_stats",
                        { inspections_audits: auditIds }
                    );
                    setReqStats(statsRes.data);
                } else {
                    setReqStats({
                        total_requirements: 0,
                        pending: 0,
                        in_progress: 0,
                        to_be_attended: 0,
                        completed: 0,
                        stats_by_inspection: [],
                    });
                }
            } catch (e) {
                console.error("BrokerReports fetch error:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    // ── Derived data ─────────────────────────────────────────────────────────

    // 1. Survey Portfolio Pie
    const surveyPieData = useMemo(
        () =>
            Object.entries(TYPE_CONFIG)
                .map(([type, cfg]) => ({
                    id: cfg.label,
                    label: cfg.label,
                    value: surveyCounts[type] || 0,
                    color: cfg.color,
                }))
                .filter((d) => d.value > 0),
        [surveyCounts]
    );

    // 2. Requirements Health Pie (actionable types only)
    const reqPieData = useMemo(() => {
        if (!reqStats?.stats_by_inspection) return [];

        const selectedTypes = reqHealthTypes.length > 0 ? reqHealthTypes : ACTIONABLE_TYPES;
        const selectedAuditIds = new Set(
            selectedTypes.flatMap((type) => (inspectionsByType[type] || []).map((i) => i.audit_id))
        );

        const totals = reqStats.stats_by_inspection
            .filter((s) => selectedAuditIds.has(s.audit_id))
            .reduce(
                (acc, s) => {
                    acc.pending += s.pending || 0;
                    acc.to_be_attended += s.to_be_attended || 0;
                    acc.in_progress += s.in_progress || 0;
                    acc.completed += s.completed || 0;
                    return acc;
                },
                { pending: 0, to_be_attended: 0, in_progress: 0, completed: 0 }
            );

        return [
            { id: "Pending",        label: "Pending",        value: totals.pending,        color: REQ_STATUS_COLORS["Pending"] },
            { id: "To be attended", label: "To be attended", value: totals.to_be_attended, color: REQ_STATUS_COLORS["To be attended"] },
            { id: "In progress",    label: "In progress",    value: totals.in_progress,    color: REQ_STATUS_COLORS["In progress"] },
            { id: "Completed",      label: "Completed",      value: totals.completed,      color: REQ_STATUS_COLORS["Completed"] },
        ].filter((d) => d.value > 0);
    }, [reqStats, inspectionsByType, reqHealthTypes]);

    const reqHealthFilterLabel = useMemo(() => {
        if (reqHealthTypes.length === ACTIONABLE_TYPES.length) {
            return "All actionable types";
        }
        return reqHealthTypes
            .map((type) => TYPE_CONFIG[type]?.label || type)
            .join(" | ");
    }, [reqHealthTypes]);

    const toggleReqHealthType = (type) => {
        setReqHealthTypes((prev) => {
            if (type === "all") return ACTIONABLE_TYPES;

            const hasType = prev.includes(type);
            if (hasType) {
                const next = prev.filter((t) => t !== type);
                return next.length > 0 ? next : ACTIONABLE_TYPES;
            }
            return [...prev, type];
        });
    };

    // 3. Completion Rate per Survey (active, overdue, escalated, submitted)
    const completionRateData = useMemo(() => {
        if (!reqStats?.stats_by_inspection) return [];
        const nonCompletedIds = new Set(
            ["active", "overdue", "escalated", "submitted"].flatMap((t) =>
                (inspectionsByType[t] || []).map((i) => i.audit_id)
            )
        );
        return reqStats.stats_by_inspection
            .filter((s) => nonCompletedIds.has(s.audit_id) && s.total_selected_requirements > 0)
            .map((s) => {
                const pct = Math.round((s.completed / s.total_selected_requirements) * 100);
                return {
                    survey:          s.audit_policy_number || String(s.audit_id),
                    label:           s.audit_policy_holder || s.audit_policy_number,
                    "% Complete":    pct,
                    "% Remaining":   100 - pct,
                    completed:       s.completed,
                    total:           s.total_selected_requirements,
                };
            })
            .sort((a, b) => a["% Complete"] - b["% Complete"])
            .slice(0, 14);
    }, [reqStats, inspectionsByType]);

    // 4. Completed Survey Duration (reminder_start_date → updated_at)
    const completionDurationData = useMemo(() => {
        const completedList = inspectionsByType.completed || [];
        return completedList
            .filter((i) => i.reminder_start_date && i.updated_at)
            .map((i) => ({
                survey:               i.audit_policy_number || String(i.audit_id),
                label:                i.audit_policy_holder || i.audit_policy_number,
                "Days to Complete":   Math.max(0, daysBetween(i.reminder_start_date, i.updated_at) ?? 0),
            }))
            .filter((d) => d["Days to Complete"] > 0)
            .sort((a, b) => b["Days to Complete"] - a["Days to Complete"])
            .slice(0, 12);
    }, [inspectionsByType]);

    // 5. Immediate Attention Table (active, overdue, escalated)
    const immediateAttentionData = useMemo(() => {
        if (!reqStats?.stats_by_inspection) return [];

        const activePolicySet     = new Set((inspectionsByType.active     || []).map((i) => i.audit_id));
        const overduePolicySet    = new Set((inspectionsByType.overdue    || []).map((i) => i.audit_id));
        const escalatedPolicySet  = new Set((inspectionsByType.escalated  || []).map((i) => i.audit_id));
        const urgentIds           = new Set([
            ...(inspectionsByType.active    || []).map((i) => i.audit_id),
            ...(inspectionsByType.overdue   || []).map((i) => i.audit_id),
            ...(inspectionsByType.escalated || []).map((i) => i.audit_id),
        ]);

        const today = todayMidnight();
        // Build a quick lookup for reminder_start_date from raw inspection data
        const inspLookup = Object.values(inspectionsByType)
            .flat()
            .reduce((acc, i) => { acc[i.audit_id] = i; return acc; }, {});

        return reqStats.stats_by_inspection
            .filter((s) => urgentIds.has(s.audit_id))
            .map((s) => {
                const insp         = inspLookup[s.audit_id];
                const startDate    = insp?.reminder_start_date ? new Date(insp.reminder_start_date) : null;
                const daysElapsed  = startDate ? Math.max(0, Math.floor((today - startDate) / (1000 * 60 * 60 * 24))) : 0;
                const unstarted    = (s.pending || 0) + (s.to_be_attended || 0);
                const urgencyScore = unstarted * 2 + daysElapsed;
                return {
                    ...s,
                    daysElapsed,
                    unstarted,
                    urgencyScore,
                    isActive:     activePolicySet.has(s.audit_id),
                    isOverdue:    overduePolicySet.has(s.audit_id),
                    isEscalated:  escalatedPolicySet.has(s.audit_id),
                };
            })
            .filter((s) => s.unstarted > 0 || s.isActive || s.isOverdue || s.isEscalated)
            .sort((a, b) => b.urgencyScore - a.urgencyScore)
            .slice(0, 12);
    }, [reqStats, inspectionsByType]);

    // 6. Response Momentum: Days Active vs % Complete
    const momentumData = useMemo(() => {
        if (!reqStats?.stats_by_inspection) return [];
        const actionableIds = new Set(
            ["active", "overdue", "escalated"].flatMap((t) =>
                (inspectionsByType[t] || []).map((i) => i.audit_id)
            )
        );
        const today = todayMidnight();
        const inspLookup = Object.values(inspectionsByType)
            .flat()
            .reduce((acc, i) => { acc[i.audit_id] = i; return acc; }, {});

        return reqStats.stats_by_inspection
            .filter((s) => actionableIds.has(s.audit_id) && s.total_selected_requirements > 0)
            .map((s) => {
                const insp        = inspLookup[s.audit_id];
                const startDate   = insp?.reminder_start_date ? new Date(insp.reminder_start_date) : null;
                const daysElapsed = startDate
                    ? Math.max(0, Math.floor((today - startDate) / (1000 * 60 * 60 * 24)))
                    : 0;
                return {
                    survey:        s.audit_policy_number || String(s.audit_id),
                    label:         s.audit_policy_holder || s.audit_policy_number,
                    "Days Active": daysElapsed,
                    "% Complete":  Math.round((s.completed / s.total_selected_requirements) * 100),
                };
            })
            .filter((d) => d["Days Active"] > 0)
            .sort((a, b) => b["Days Active"] - a["Days Active"])
            .slice(0, 12);
    }, [reqStats, inspectionsByType]);

    // ── Nivo shared theme factory ─────────────────────────────────────────────

    const nivoTheme = {
        axis: {
            ticks: { text: { fill: colors.grey[400], fontSize: 11 } },
            legend: { text: { fill: colors.grey[400] } },
        },
        grid:    { line: { stroke: colors.grey[700] } },
        legends: { text: { fill: colors.grey[300] } },
        tooltip: {
            container: {
                background: colors.grey[900],
                color: colors.grey[100],
                fontSize: 12,
                border: `1px solid ${colors.grey[600]}`,
            },
        },
    };

    const pieLegends = [
        {
            anchor: "right",
            direction: "column",
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 6,
            itemWidth: 100,
            itemHeight: 18,
            itemTextColor: colors.grey[100],
            itemDirection: "left-to-right",
            itemOpacity: 1,
            symbolSize: 12,
            symbolShape: "circle",
        },
    ];

    const completionRateTooltip = useMemo(() => createCompletionRateTooltip(colors), [colors]);
    const completionDurationTooltip = useMemo(() => createCompletionDurationTooltip(colors), [colors]);
    const momentumTooltip = useMemo(() => createMomentumTooltip(colors), [colors]);

    const completionRateLeftMargin = useMemo(
        () => getHorizontalChartLeftMargin(completionRateData, "survey"),
        [completionRateData]
    );
    const completionDurationLeftMargin = useMemo(
        () => getHorizontalChartLeftMargin(completionDurationData, "survey"),
        [completionDurationData]
    );
    const momentumLeftMargin = useMemo(
        () => getHorizontalChartLeftMargin(momentumData, "survey"),
        [momentumData]
    );

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box m="20px">
            <Header
                title="MY REPORTS"
                subtitle="Statistical overview of survey portfolio and requirements management"
            />

            {/* ── 1. KPI Cards ───────────────────────────────────────────────── */}
            <Grid container spacing={2} mt={1}>
                {Object.entries(TYPE_CONFIG).map(([type, cfg]) => (
                    <Grid item xs={6} sm={4} md key={type}>
                        <Card
                            variant="outlined"
                            sx={{
                                borderTop: `3px solid ${cfg.color}`,
                                borderRadius: 0,
                                bgcolor: colors.primary[400],
                            }}
                        >
                            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                                <Typography
                                    variant="caption"
                                    color={colors.grey[400]}
                                    textTransform="uppercase"
                                    letterSpacing="0.06em"
                                    display="block"
                                >
                                    {cfg.label}
                                </Typography>
                                <Typography variant="h3" color={cfg.color} fontWeight="bold" mt={0.5}>
                                    {surveyCounts[type] || 0}
                                </Typography>
                                <Typography variant="caption" color={colors.grey[500]}>
                                    {surveyCounts[type] === 1 ? "survey" : "surveys"}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* ── 2. Two Donut Charts ─────────────────────────────────────────── */}
            <Grid container spacing={2} mt={1}>
                {/* Survey Portfolio */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 0, bgcolor: colors.primary[400], height: "100%" }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AssessmentOutlinedIcon sx={{ color: colors.blueAccent[400] }} />
                                <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                                    Survey Portfolio
                                </Typography>
                            </Box>
                            <Divider sx={{ borderColor: colors.grey[700], my: 1.5 }} />
                            {surveyPieData.length > 0 ? (
                                <Box height={290}>
                                    <ResponsivePie
                                        data={surveyPieData}
                                        margin={{ top: 20, right: 90, bottom: 20, left: 90 }}
                                        innerRadius={0.55}
                                        padAngle={0.7}
                                        cornerRadius={3}
                                        colors={{ datum: "data.color" }}
                                        borderWidth={1}
                                        borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                                        enableArcLinkLabels={false}
                                        enableArcLabels={false}
                                        legends={pieLegends}
                                        theme={nivoTheme}
                                    />
                                </Box>
                            ) : (
                                <Typography color={colors.grey[500]} variant="body2" mt={2}>
                                    No survey data available.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Requirements Health */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 0, bgcolor: colors.primary[400], height: "100%" }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AssessmentOutlinedIcon sx={{ color: colors.greenAccent?.[400] || "#4cceac" }} />
                                <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                                    Overall Requirements Health
                                </Typography>
                            </Box>
                            <Typography variant="caption" color={colors.grey[500]}>
                                Across active, overdue, escalated, submitted &amp; completed surveys
                            </Typography>
                            <Box display="flex" gap={0.8} flexWrap="wrap" mt={1.2}>
                                {(() => {
                                    const allSelected = reqHealthTypes.length === ACTIONABLE_TYPES.length;
                                    return (
                                        <Chip
                                            label="All"
                                            size="small"
                                            icon={allSelected ? <CheckIcon sx={{ fontSize: 14, color: colors.blueAccent[300] }} /> : undefined}
                                            onClick={() => toggleReqHealthType("all")}
                                            sx={{
                                                cursor: "pointer",
                                                bgcolor: `${colors.blueAccent[500]}22`,
                                                color: colors.blueAccent[200],
                                                border: `1px solid ${allSelected ? colors.blueAccent[400] : colors.grey[700]}`,
                                                fontWeight: 700,
                                                transition: "border-color 0.15s ease",
                                                "& .MuiChip-icon": { ml: "6px" },
                                                "&:hover": { borderColor: colors.blueAccent[300] },
                                            }}
                                        />
                                    );
                                })()}
                                {ACTIONABLE_TYPES.map((type) => {
                                    const selected = reqHealthTypes.includes(type);
                                    const cfg = TYPE_CONFIG[type];
                                    return (
                                        <Chip
                                            key={type}
                                            label={cfg.label}
                                            size="small"
                                            icon={selected ? <CheckIcon sx={{ fontSize: 14, color: cfg.color }} /> : undefined}
                                            onClick={() => toggleReqHealthType(type)}
                                            sx={{
                                                cursor: "pointer",
                                                bgcolor: `${cfg.color}22`,
                                                color: cfg.color,
                                                border: `1px solid ${selected ? cfg.color : colors.grey[700]}`,
                                                fontWeight: 700,
                                                transition: "border-color 0.15s ease",
                                                "& .MuiChip-icon": { ml: "6px" },
                                                "&:hover": { borderColor: cfg.color },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                            <Typography variant="caption" color={colors.grey[400]} display="block" mt={0.8}>
                                Showing: {reqHealthFilterLabel}
                            </Typography>
                            <Divider sx={{ borderColor: colors.grey[700], my: 1.5 }} />
                            {reqPieData.length > 0 ? (
                                <Box height={290}>
                                    <ResponsivePie
                                        data={reqPieData}
                                        margin={{ top: 20, right: 90, bottom: 20, left: 90 }}
                                        innerRadius={0.55}
                                        padAngle={0.7}
                                        cornerRadius={3}
                                        colors={{ datum: "data.color" }}
                                        borderWidth={1}
                                        borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                                        enableArcLinkLabels={false}
                                        enableArcLabels={false}
                                        legends={pieLegends}
                                        theme={nivoTheme}
                                    />
                                </Box>
                            ) : (
                                <Typography color={colors.grey[500]} variant="body2" mt={2}>
                                    No active requirement data available.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ── 3. Immediate Attention Table ────────────────────────────────── */}
            {immediateAttentionData.length > 0 && (
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Card
                            variant="outlined"
                            sx={{ borderLeft: "4px solid #db4f4a", borderRadius: 0, bgcolor: colors.primary[400] }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <WarningAmberIcon sx={{ color: "#db4f4a", fontSize: 22 }} />
                                    <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                                        Requires Immediate Attention
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color={colors.grey[500]}>
                                    Across active, overdue, and escalated. Surveys ranked by Urgency Score calculated as (unstarted requirements × 2) + days since first reminder.
                                </Typography>
                                <Divider sx={{ borderColor: colors.grey[700], my: 1.5 }} />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    "Policy Holder",
                                                    "Policy No",
                                                    "Days Active",
                                                    "Unstarted",
                                                    "In Progress",
                                                    "Completed",
                                                    "Status",
                                                ].map((h) => (
                                                    <TableCell
                                                        key={h}
                                                        sx={{ color: colors.grey[400], fontWeight: "bold", fontSize: "0.72rem", py: 0.8 }}
                                                    >
                                                        {h}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {immediateAttentionData.map((row) => (
                                                (() => {
                                                    const daysElapsedColors = getDaysElapsedColors(row.daysElapsed);
                                                    const unstartedColor = getUnstartedColor(row.unstarted, colors);
                                                    return (
                                                <TableRow key={row.audit_id}>
                                                    <TableCell sx={{ color: colors.grey[100], fontSize: "0.8rem", py: 0.8 }}>
                                                        {row.audit_policy_holder}
                                                    </TableCell>
                                                    <TableCell sx={{ color: colors.grey[300], fontSize: "0.8rem", py: 0.8 }}>
                                                        {row.audit_policy_number}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8 }}>
                                                        <Chip
                                                            label={`${row.daysElapsed}d`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: daysElapsedColors.bgColor,
                                                                color: daysElapsedColors.textColor,
                                                                fontWeight: 700,
                                                                fontSize: "0.7rem",
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8 }}>
                                                        <Typography
                                                            fontWeight="bold"
                                                            fontSize="0.85rem"
                                                            color={unstartedColor}
                                                        >
                                                            {row.unstarted}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ color: "#FF9800", fontWeight: "bold", fontSize: "0.85rem", py: 0.8 }}>
                                                        {row.in_progress}
                                                    </TableCell>
                                                    <TableCell sx={{ color: "#4cceac", fontWeight: "bold", fontSize: "0.85rem", py: 0.8 }}>
                                                        {row.completed} / {row.total_selected_requirements}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8 }}>
                                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                                            {row.isActive && (
                                                                <Chip
                                                                    label="Active"
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: "#4cceac22",
                                                                        color: "#4cceac",
                                                                        fontWeight: 600,
                                                                        fontSize: "0.65rem",
                                                                        height: 20,
                                                                    }}
                                                                />
                                                            )}
                                                            {row.isOverdue && (
                                                                <Chip
                                                                    label="Overdue"
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: "#db4f4a22",
                                                                        color: "#db4f4a",
                                                                        fontWeight: 600,
                                                                        fontSize: "0.65rem",
                                                                        height: 20,
                                                                    }}
                                                                />
                                                            )}
                                                            {row.isEscalated && (
                                                                <Chip
                                                                    label="Escalated"
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: "#FF980022",
                                                                        color: "#FF9800",
                                                                        fontWeight: 600,
                                                                        fontSize: "0.65rem",
                                                                        height: 20,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                    );
                                                })()
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* ── 4. Completion Rate by Survey ────────────────────────────────── */}
            {completionRateData.length > 0 && (
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 0, bgcolor: colors.primary[400] }}>
                            <CardContent>
                                <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
                                    Requirements Completion Rate by Survey
                                </Typography>
                                <Typography variant="caption" color={colors.grey[500]}>
                                    Active · Overdue · Escalated · Submitted surveys — sorted from least complete to most.
                                    Green ≥ 75 % · Orange ≥ 40 % · Red &lt; 40 %
                                </Typography>
                                <Divider sx={{ borderColor: colors.grey[700], my: 1.5 }} />
                                <Box height={Math.max(240, completionRateData.length * 38 + 60)}>
                                    <ResponsiveBar
                                        data={completionRateData}
                                        keys={["% Complete", "% Remaining"]}
                                        indexBy="survey"
                                        layout="horizontal"
                                        margin={{ top: 10, right: 70, bottom: 44, left: completionRateLeftMargin }}
                                        padding={0.3}
                                        maxValue={100}
                                        colors={(bar) => {
                                            if (bar.id === "% Remaining") return "rgba(255,255,255,0.07)";
                                            const v = bar.data["% Complete"];
                                            if (v >= 75) return "#4cceac";
                                            if (v >= 40) return "#FF9800";
                                            return "#db4f4a";
                                        }}
                                        borderWidth={0}
                                        borderRadius={3}
                                        axisBottom={{
                                            tickSize: 4,
                                            tickPadding: 5,
                                            legend: "% Complete",
                                            legendPosition: "middle",
                                            legendOffset: 36,
                                            tickValues: [0, 25, 50, 75, 100],
                                            format: (v) => `${v}%`,
                                        }}
                                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                        label={(bar) => (bar.id === "% Complete" ? `${bar.value}%` : "")}
                                        labelSkipWidth={24}
                                        labelTextColor="#fff"
                                        enableLabel
                                        theme={nivoTheme}
                                        tooltip={completionRateTooltip}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* ── 5. Completed Survey Duration ────────────────────────────────── */}
            {completionDurationData.length > 0 && (
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 0, bgcolor: colors.primary[400] }}>
                            <CardContent>
                                <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
                                    Completed Surveys — Days to Full Completion
                                </Typography>
                                <Typography variant="caption" color={colors.grey[500]}>
                                    Days from first reminder email sent to survey sign-off. Shorter is better.
                                </Typography>
                                <Divider sx={{ borderColor: colors.grey[700], my: 1.5 }} />
                                <Box height={Math.max(240, completionDurationData.length * 38 + 60)}>
                                    <ResponsiveBar
                                        data={completionDurationData}
                                        keys={["Days to Complete"]}
                                        indexBy="survey"
                                        layout="horizontal"
                                        margin={{ top: 10, right: 70, bottom: 44, left: completionDurationLeftMargin }}
                                        padding={0.3}
                                        colors="#2e7c67"
                                        borderRadius={3}
                                        axisBottom={{
                                            tickSize: 4,
                                            tickPadding: 5,
                                            legend: "Days",
                                            legendPosition: "middle",
                                            legendOffset: 36,
                                        }}
                                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                        labelSkipWidth={28}
                                        labelTextColor="#fff"
                                        theme={nivoTheme}
                                        tooltip={completionDurationTooltip}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* ── 6. Response Momentum ────────────────────────────────────────── */}
            {momentumData.length > 0 && (
                <Grid container spacing={2} mt={1} mb={4}>
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 0, bgcolor: colors.primary[400] }}>
                            <CardContent>
                                <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
                                    Response Momentum — Days Active vs Completion Progress
                                </Typography>
                                <Typography variant="caption" color={colors.grey[500]}>
                                    Active · Overdue · Escalated surveys. High days-active with low % complete = most at-risk.
                                </Typography>
                                <Divider sx={{ borderColor: colors.grey[700], my: 1.5 }} />
                                <Box height={Math.max(240, momentumData.length * 50 + 70)}>
                                    <ResponsiveBar
                                        data={momentumData}
                                        keys={["Days Active", "% Complete"]}
                                        indexBy="survey"
                                        layout="horizontal"
                                        groupMode="grouped"
                                        margin={{ top: 10, right: 120, bottom: 50, left: momentumLeftMargin }}
                                        padding={0.2}
                                        colors={["#6870fa", "#4cceac"]}
                                        borderRadius={2}
                                        axisBottom={{
                                            tickSize: 4,
                                            tickPadding: 5,
                                            legend: "Value",
                                            legendPosition: "middle",
                                            legendOffset: 40,
                                        }}
                                        axisLeft={{ tickSize: 0, tickPadding: 8 }}
                                        labelSkipWidth={22}
                                        labelTextColor="#fff"
                                        legends={[
                                            {
                                                dataFrom: "keys",
                                                anchor: "bottom-right",
                                                direction: "column",
                                                translateX: 110,
                                                translateY: 0,
                                                itemWidth: 105,
                                                itemHeight: 20,
                                                itemTextColor: colors.grey[300],
                                                symbolSize: 12,
                                                symbolShape: "circle",
                                            },
                                        ]}
                                        theme={nivoTheme}
                                        tooltip={momentumTooltip}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
